'use client';

import { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { getGameAssetUrl } from '@/lib/assets';
import { apiErrorMessage } from '@/lib/api-errors';
import {
  formatKd,
  formatPlaytime,
  formatWinRate,
  getPrimaryInputStats,
  type FortnitePlayerStats,
} from '@/lib/fortnite-api';
import type { FortniteProfile } from '@/lib/supabase';

const CACHE_TTL_MS = 15 * 60 * 1000;
const fortniteCache = new Map<string, { expiresAt: number; data: FortnitePlayerStats }>();
const fortniteInflight = new Map<string, Promise<FortnitePlayerStats>>();

interface FortniteStatsProps {
  playerId: string;
  /**
   * Pre-synced row from `fortnite_profiles`, refreshed daily by the
   * fortnite-sync cron. When present the component renders straight from it and
   * never calls the API — no request per page view, and it still works while
   * upstream is down. Falls back to the live fetch when absent (or when the row
   * has never synced), so callers that don't join the table keep working.
   */
  profile?: FortniteProfile | null;
}

/**
 * Re-shape a synced DB row into the same structure the live API returns, so the
 * renderer below has exactly one input shape to handle.
 *
 * The row stores a single flattened rollup, so it maps onto `all` with the
 * per-mode splits left null — the card doesn't render them.
 */
function profileToStats(profile: FortniteProfile): FortnitePlayerStats | null {
  if (profile.matches == null) return null;
  const overall = {
    score: profile.score ?? 0,
    scorePerMin: 0,
    scorePerMatch: 0,
    wins: profile.wins ?? 0,
    top3: profile.top3 ?? undefined,
    top5: profile.top5 ?? undefined,
    top10: profile.top10 ?? undefined,
    top25: profile.top25 ?? undefined,
    kills: profile.kills ?? 0,
    killsPerMin: 0,
    killsPerMatch: 0,
    deaths: profile.deaths ?? 0,
    kd: profile.kd ?? 0,
    matches: profile.matches ?? 0,
    winRate: profile.win_rate ?? 0,
    minutesPlayed: profile.minutes_played ?? 0,
    playersOutlived: profile.players_outlived ?? 0,
  };

  return {
    account: { id: profile.account_id ?? '', name: profile.account_name ?? '' },
    battlePass: {
      level: profile.battle_pass_level ?? 0,
      progress: profile.battle_pass_progress ?? null,
    },
    image: null,
    stats: { all: { overall }, keyboardMouse: null, gamepad: null, touch: null },
  };
}

export default function FortniteStats({ playerId, profile }: FortniteStatsProps) {
  // A synced row that actually has stats short-circuits the fetch entirely.
  const synced = profile ? profileToStats(profile) : null;

  const [stats, setStats] = useState<FortnitePlayerStats | null>(synced);
  // A private row has no stats but is still a resolved state, so it must not
  // start in `loading` -- otherwise the server renders a skeleton and only the
  // client-side effect reveals the explanation.
  const [loading, setLoading] = useState(!synced && !profile?.stats_private);
  const [error, setError] = useState<string | null>(null);
  // 403 means the account exists but hides its match history — worth telling the
  // player, since it's the one failure they can actually fix themselves.
  const [statsArePrivate, setStatsArePrivate] = useState(
    Boolean(profile?.stats_private)
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Synced data already rendered; don't spend a request re-fetching it.
      if (synced || profile?.stats_private) {
        setStats(synced);
        setStatsArePrivate(Boolean(profile?.stats_private));
        setError(null);
        setLoading(false);
        return;
      }

      if (!playerId) {
        setError('Missing player');
        setStatsArePrivate(false);
        setLoading(false);
        return;
      }

      const cached = fortniteCache.get(playerId);
      if (cached && cached.expiresAt > Date.now()) {
        setStats(cached.data);
        setError(null);
        setStatsArePrivate(false);
        setLoading(false);
        return;
      }

      let pending = fortniteInflight.get(playerId);
      if (!pending) {
        pending = (async () => {
          const response = await fetch(
            `/api/fortnite-stats?player=${encodeURIComponent(playerId)}`
          );
          if (!response.ok) {
            const body = await response.json().catch(() => null);
            const err = new Error(apiErrorMessage(body, 'Failed to load stats'));
            (err as Error & { status?: number }).status = response.status;
            throw err;
          }
          const data: FortnitePlayerStats = await response.json();
          fortniteCache.set(playerId, { expiresAt: Date.now() + CACHE_TTL_MS, data });
          return data;
        })().finally(() => {
          fortniteInflight.delete(playerId);
        });
        fortniteInflight.set(playerId, pending);
      }

      try {
        setLoading(true);
        setError(null);
        setStatsArePrivate(false);
        const data = await pending;
        if (!cancelled) setStats(data);
      } catch (e) {
        if (cancelled) return;
        const status = (e as Error & { status?: number }).status;
        setStatsArePrivate(status === 403);
        setError(e instanceof Error ? e.message : 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // `synced` is a fresh object each render, so depend on the stable fields it
    // derives from rather than the object itself.
  }, [playerId, profile?.matches, profile?.stats_private, profile?.last_synced_at]);

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 via-transparent to-white/5 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-white/10 rounded animate-pulse w-32" />
            <div className="h-3 bg-white/10 rounded animate-pulse w-24" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-white/10 rounded animate-pulse" />
          <div className="h-3 bg-white/10 rounded animate-pulse w-4/5" />
        </div>
      </div>
    );
  }

  if (statsArePrivate) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 via-transparent to-white/5 p-5">
        <div className="absolute inset-0 opacity-30 blur-3xl bg-gradient-to-r from-primary/20 to-accent/20" />
        <div className="relative flex items-center gap-3">
          <div className="w-14 h-14 shrink-0 rounded-xl bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center">
            <Lock size={20} className="text-white/70" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-medium text-white font-space-mono">Fortnite</p>
            <p className="text-sm text-white/60">
              Stats are private. Turn on public match history in Fortnite under
              Settings → Account &amp; Privacy to show them here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return null;
  }

  const input = getPrimaryInputStats(stats.stats);
  if (!input) {
    return null;
  }

  const { overall } = input;
  const overallKd = formatKd(overall);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 via-transparent to-white/5 p-5">
      <div className="absolute inset-0 opacity-30 blur-3xl bg-gradient-to-r from-primary/20 to-accent/20" />
      <div className="relative space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-white/10">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={getGameAssetUrl('Fortnite')}
              alt="Fortnite"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium text-white truncate font-space-mono">Fortnite</p>
            {stats.battlePass?.level ? (
              <p className="text-sm text-white/60 truncate">
                Level {stats.battlePass.level}
              </p>
            ) : null}
          </div>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/50">Wins</p>
            <p className="text-lg font-semibold text-white">{overall.wins.toLocaleString()}</p>
            <p className="text-xs text-white/50">{formatWinRate(overall.winRate)} win rate</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/50">
              {overallKd ? 'K/D' : 'Kills'}
            </p>
            <p className="text-lg font-semibold text-white">
              {overallKd ?? overall.kills.toLocaleString()}
            </p>
            <p className="text-xs text-white/50">
              {overallKd ? `${overall.kills.toLocaleString()} kills` : 'No deaths yet'}
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/50">Matches</p>
            <p className="text-lg font-semibold text-white">{overall.matches.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/50">Played</p>
            <p className="text-lg font-semibold text-white">{formatPlaytime(overall.minutesPlayed)}</p>
          </div>
        </div>

      </div>
    </div>
  );
}
